import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

import { getInitiativeById } from '@/features/canvas/api';
import {
  createBudgetDropdownVisible,
  getBudgetDropdownsVisible,
} from '@/features/canvas/api/budget-dropdown-visible';
import {
  deleteBudgetPopupVisible,
  getBudgetPopupsVisible,
} from '@/features/canvas/api/budget-popup-visible';
import {
  BudgetRoadmapVisible,
  deleteBudgetRoadmapVisible,
  getBudgetRoadmapVisible,
} from '@/features/canvas/api/budget-roadmap-visible';
import {
  deleteBudgetField,
  getAllBudgetsForRoadmap,
} from '@/features/canvas/api/initiative_budgets';
import { DualChoiceDialog } from '@/features/canvas/components/DualChoiceDialog';
import { useEditorContext } from '@/features/canvas/contexts/editor-context';
import { useShowToast } from '@/hooks/useShowToast';

type BudgetFieldDeleteDialogProps = {
  fieldId: number | null;
  fieldTitle: string;
  fieldType: 'big' | 'small';
  initiativeId: number;
  onClose: () => void;
  open: boolean;
};

export const BudgetFieldDeleteDialog = ({
  open,
  onClose,
  fieldId,
  fieldTitle,
  fieldType,
  initiativeId,
}: BudgetFieldDeleteDialogProps) => {
  const { t } = useTranslation();
  const { showToast } = useShowToast();
  const { budgetPopupVisible, roadmap, updateLastSavedTime } = useEditorContext();
  const queryClient = useQueryClient();

  // Fetch budget roadmap visible data
  const { data: budgetRoadmapVisible = [] } = useQuery(
    [getBudgetRoadmapVisible.name, roadmap.id],
    () => getBudgetRoadmapVisible({ roadmapId: roadmap.id! }),
    {
      enabled: Boolean(roadmap.id),
    }
  );

  // Delete from current roadmap only (move to dropdown)
  const { mutateAsync: deleteFromCurrentRoadmap, isLoading: isDeletingFromCurrent } = useMutation(
    async () => {
      if (!fieldId) return;

      try {
        const actions: Promise<unknown>[] = [];

        // Find matching visibility entries
        const popupToDelete = budgetPopupVisible?.find(
          p => p.title === fieldTitle && p.type === fieldType
        );
        const roadmapToDelete = budgetRoadmapVisible?.find(
          (r: BudgetRoadmapVisible) => r.title === fieldTitle && r.type === fieldType
        );

        // Remove from visibility tables
        if (popupToDelete) {
          actions.push(deleteBudgetPopupVisible({ fieldId: popupToDelete.id }));
        }
        if (roadmapToDelete) {
          actions.push(deleteBudgetRoadmapVisible({ fieldId: roadmapToDelete.id }));
        }

        // Add to dropdown for recovery
        actions.push(
          createBudgetDropdownVisible({
            roadmapId: roadmap.id!,
            newDropdownVisible: { title: fieldTitle, type: fieldType },
          })
        );

        await Promise.all(actions);
        await queryClient.invalidateQueries([getBudgetPopupsVisible.name, roadmap.id]);
        await queryClient.invalidateQueries([getBudgetRoadmapVisible.name, roadmap.id]);
        await queryClient.invalidateQueries([getBudgetDropdownsVisible.name, roadmap.id]);
        await queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmap.id]);

        updateLastSavedTime();
        showToast('success', t('editor.sidebar.initiative.budget.delete_field_success'));
      } catch (error) {
        showToast('error', t('editor.sidebar.initiative.budget.delete_field_error'));
        throw error;
      }
    }
  );

  // Delete from all roadmaps permanently
  const { mutateAsync: deleteFromAllRoadmaps, isLoading: isDeletingFromAll } = useMutation(
    async () => {
      if (!fieldId) return;

      try {
        await deleteBudgetField({ initiativeId, fieldId });

        // Invalidate all relevant queries
        await queryClient.invalidateQueries([getBudgetPopupsVisible.name, roadmap.id]);
        await queryClient.invalidateQueries([getBudgetRoadmapVisible.name, roadmap.id]);
        await queryClient.invalidateQueries([getBudgetDropdownsVisible.name, roadmap.id]);
        await queryClient.invalidateQueries([getAllBudgetsForRoadmap.name, roadmap.id]);
        await queryClient.invalidateQueries([getInitiativeById.name, initiativeId]);

        updateLastSavedTime();
        showToast('success', t('editor.sidebar.initiative.budget.custom_field_deleted'));
      } catch (error) {
        showToast('error', t('editor.sidebar.initiative.budget.delete_field_error'));
        throw error;
      }
    }
  );

  const handleDeleteFromCurrentRoadmap = async () => {
    await deleteFromCurrentRoadmap();
    onClose();
  };

  const handleDeleteFromAllRoadmaps = async () => {
    await deleteFromAllRoadmaps();
    onClose();
  };

  const isLoading = isDeletingFromCurrent || isDeletingFromAll;

  return (
    <DualChoiceDialog
      id="delete-budget-custom-field-dialog"
      open={open && Boolean(fieldId)}
      onClose={onClose}
      dialogDescription={t('editor.delete_custom_field_dialog.description')}
      firstChoiceOnClick={handleDeleteFromCurrentRoadmap}
      firstChoiceIsDisabled={isLoading}
      firstChoiceIsLoading={isDeletingFromCurrent}
      firstChoiceButtonText={t('editor.delete_custom_field_dialog.this_roadmap_only')}
      firstChoiceDescription={t('editor.delete_custom_field_dialog.this_roadmap_only_description')}
      secondChoiceOnClick={handleDeleteFromAllRoadmaps}
      secondChoiceIsDisabled={isLoading}
      secondChoiceIsLoading={isDeletingFromAll}
      secondChoiceButtonText={t('editor.delete_custom_field_dialog.all_roadmaps')}
      secondChoiceDescription={t('editor.delete_custom_field_dialog.all_roadmaps_description')}
      keepMounted={false}
    />
  );
};
